// D3 Stacked/Grouped Bar Chart with Toggle for BodyOS data visualizations
// Animated transition between stacked and grouped views
import * as d3 from 'd3'
import {
  colors,
  fonts,
  animationConfig,
  defaultMargins,
  defaultDimensions
} from './config'
import {
  createSvg,
  createTooltip,
  showTooltipHtml,
  hideTooltip,
  createLegend,
  debounce
} from './utils'

export default class StackedGroupedBarChart {
  constructor(container, options = {}) {
    this.container = typeof container === 'string' ? document.querySelector(container) : container
    this.options = {
      width: defaultDimensions.width,
      height: defaultDimensions.height,
      margins: { ...defaultMargins, top: 80 },
      xLabel: '',
      yLabel: '',
      showLegend: true,
      showValues: false,
      animate: true,
      responsive: true,
      initialView: 'grouped', // 'stacked' or 'grouped'
      showToggle: true,
      barPadding: 0.2,
      groupPadding: 0.1,
      ...options
    }

    this.data = []
    this.svg = null
    this.tooltip = null
    this.currentView = this.options.initialView
    this.g = null
    this.xScale = null
    this.yScale = null
    this.x1Scale = null
    this.colorScale = null
    this.keys = []
    this.innerWidth = 0
    this.innerHeight = 0

    if (this.options.responsive) {
      this.handleResize = debounce(() => this.render(), 250)
      window.addEventListener('resize', this.handleResize)
    }
  }

  setData(data) {
    this.data = data
    return this
  }

  render() {
    this.container.innerHTML = ''

    if (!this.data || this.data.length === 0) {
      this.renderEmptyState()
      return this
    }

    const containerWidth = this.container.clientWidth || this.options.width
    const width = Math.min(containerWidth, this.options.width)
    const height = (width / this.options.width) * this.options.height

    const { svg, g, innerWidth, innerHeight } = createSvg(
      this.container,
      width,
      height,
      this.options.margins
    )
    this.svg = svg
    this.g = g
    this.innerWidth = innerWidth
    this.innerHeight = innerHeight

    this.tooltip = createTooltip(this.container)

    // Extract keys and set up scales
    this.keys = this.data[0].values.map((v) => v.name)

    // X scale (categories)
    this.xScale = d3.scaleBand()
      .domain(this.data.map((d) => d.category))
      .range([0, innerWidth])
      .padding(this.options.barPadding)

    // X1 scale (for grouped bars)
    this.x1Scale = d3.scaleBand()
      .domain(this.keys)
      .range([0, this.xScale.bandwidth()])
      .padding(this.options.groupPadding)

    // Color scale
    this.colorScale = d3.scaleOrdinal()
      .domain(this.keys)
      .range(this.keys.map((k, i) => {
        const val = this.data[0].values.find((v) => v.name === k)
        return val?.color || colors.chart[i % colors.chart.length]
      }))

    // Calculate max values for both views
    const maxStacked = d3.max(this.data, (d) => d3.sum(d.values, (v) => v.value))
    const maxGrouped = d3.max(this.data, (d) => d3.max(d.values, (v) => v.value))

    // Y scale (start with current view's max)
    const yMax = this.currentView === 'stacked' ? maxStacked : maxGrouped
    this.yScale = d3.scaleLinear()
      .domain([0, yMax * 1.1])
      .range([innerHeight, 0])
      .nice()

    // Store max values for transitions
    this.maxStacked = maxStacked
    this.maxGrouped = maxGrouped

    // Draw axes
    this.drawAxes()

    // Draw toggle button
    if (this.options.showToggle) {
      this.drawToggle()
    }

    // Draw bars
    this.drawBars()

    // Legend
    if (this.options.showLegend) {
      const legendItems = this.keys.map((k) => ({
        label: k,
        color: this.colorScale(k)
      }))
      createLegend(g, legendItems, {
        x: 0,
        y: -60,
        direction: 'horizontal'
      })
    }

    return this
  }

  drawAxes() {
    const { g, xScale, yScale, innerWidth, innerHeight } = this

    // Remove existing axes
    g.selectAll('.x-axis, .y-axis').remove()

    // X axis
    const xAxis = g.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0, ${innerHeight})`)
      .call(d3.axisBottom(xScale))

    xAxis.selectAll('text')
      .attr('fill', colors.textMuted)
      .attr('font-size', fonts.sizes.sm)
      .attr('font-family', fonts.family)

    xAxis.selectAll('line, path')
      .attr('stroke', colors.axis)
      .attr('stroke-width', 2)

    if (this.options.xLabel) {
      g.append('text')
        .attr('class', 'x-label')
        .attr('x', innerWidth / 2)
        .attr('y', innerHeight + 45)
        .attr('text-anchor', 'middle')
        .attr('fill', colors.text)
        .attr('font-size', fonts.sizes.base)
        .attr('font-weight', fonts.weights.medium)
        .text(this.options.xLabel.toUpperCase())
    }

    // Y axis
    const yAxis = g.append('g')
      .attr('class', 'y-axis')
      .call(d3.axisLeft(yScale))

    yAxis.selectAll('text')
      .attr('fill', colors.textMuted)
      .attr('font-size', fonts.sizes.sm)
      .attr('font-family', fonts.family)

    yAxis.selectAll('line, path')
      .attr('stroke', colors.axis)
      .attr('stroke-width', 2)

    if (this.options.yLabel) {
      g.append('text')
        .attr('class', 'y-label')
        .attr('transform', 'rotate(-90)')
        .attr('x', -innerHeight / 2)
        .attr('y', -45)
        .attr('text-anchor', 'middle')
        .attr('fill', colors.text)
        .attr('font-size', fonts.sizes.base)
        .attr('font-weight', fonts.weights.medium)
        .text(this.options.yLabel.toUpperCase())
    }

    // Grid lines
    g.append('g')
      .attr('class', 'grid-y')
      .call(d3.axisLeft(yScale)
        .ticks(5)
        .tickSize(-innerWidth)
        .tickFormat(''))
      .selectAll('line')
      .attr('stroke', colors.grid)
      .attr('stroke-width', 1)

    g.selectAll('.grid-y path').remove()
  }

  drawToggle() {
    const { g, innerWidth } = this

    const toggleGroup = g.append('g')
      .attr('class', 'toggle-group')
      .attr('transform', `translate(${innerWidth - 180}, -40)`)

    // Toggle background
    toggleGroup.append('rect')
      .attr('width', 180)
      .attr('height', 28)
      .attr('rx', 4)
      .attr('fill', colors.neutral[100])
      .attr('stroke', colors.neutral[300])
      .attr('stroke-width', 1)

    // Stacked button
    const stackedBtn = toggleGroup.append('g')
      .attr('class', 'toggle-btn toggle-stacked')
      .style('cursor', 'pointer')
      .on('click', () => this.toggleView('stacked'))

    stackedBtn.append('rect')
      .attr('x', 2)
      .attr('y', 2)
      .attr('width', 86)
      .attr('height', 24)
      .attr('rx', 3)
      .attr('fill', this.currentView === 'stacked' ? colors.neutral[900] : 'transparent')

    stackedBtn.append('text')
      .attr('x', 45)
      .attr('y', 18)
      .attr('text-anchor', 'middle')
      .attr('fill', this.currentView === 'stacked' ? colors.neutral.white : colors.text)
      .attr('font-size', fonts.sizes.xs)
      .attr('font-family', fonts.family)
      .attr('font-weight', fonts.weights.medium)
      .text('STACKED')

    // Grouped button
    const groupedBtn = toggleGroup.append('g')
      .attr('class', 'toggle-btn toggle-grouped')
      .style('cursor', 'pointer')
      .on('click', () => this.toggleView('grouped'))

    groupedBtn.append('rect')
      .attr('x', 92)
      .attr('y', 2)
      .attr('width', 86)
      .attr('height', 24)
      .attr('rx', 3)
      .attr('fill', this.currentView === 'grouped' ? colors.neutral[900] : 'transparent')

    groupedBtn.append('text')
      .attr('x', 135)
      .attr('y', 18)
      .attr('text-anchor', 'middle')
      .attr('fill', this.currentView === 'grouped' ? colors.neutral.white : colors.text)
      .attr('font-size', fonts.sizes.xs)
      .attr('font-family', fonts.family)
      .attr('font-weight', fonts.weights.medium)
      .text('GROUPED')
  }

  drawBars() {
    const { g, data, xScale, x1Scale, yScale, colorScale, keys, innerHeight } = this

    // Prepare stacked data
    const stackGenerator = d3.stack()
      .keys(keys)
      .value((d, key) => {
        const val = d.values.find((v) => v.name === key)
        return val ? val.value : 0
      })

    const stackedData = stackGenerator(data)

    // Create bar groups for each category
    const categoryGroups = g.selectAll('.category-group')
      .data(data)
      .enter()
      .append('g')
      .attr('class', 'category-group')
      .attr('transform', (d) => `translate(${xScale(d.category)}, 0)`)

    // Draw bars based on current view
    if (this.currentView === 'stacked') {
      this.drawStackedBars(categoryGroups, stackedData, xScale, yScale, colorScale, innerHeight)
    } else {
      this.drawGroupedBars(categoryGroups, x1Scale, yScale, colorScale, innerHeight)
    }
  }

  drawStackedBars(categoryGroups, stackedData, xScale, yScale, colorScale, innerHeight) {
    const self = this

    categoryGroups.each(function(categoryData, categoryIndex) {
      const group = d3.select(this)

      stackedData.forEach((layer, layerIndex) => {
        const d = layer[categoryIndex]

        const bar = group.append('rect')
          .attr('class', `bar bar-${layerIndex}`)
          .attr('x', 0)
          .attr('width', xScale.bandwidth())
          .attr('fill', colorScale(layer.key))
          .attr('stroke', colors.neutral[900])
          .attr('stroke-width', 1.5)
          .style('cursor', 'pointer')
          .datum({ ...d, key: layer.key, category: categoryData.category })

        if (self.options.animate) {
          bar
            .attr('y', innerHeight)
            .attr('height', 0)
            .transition()
            .duration(animationConfig.duration)
            .delay(layerIndex * 50)
            .attr('y', yScale(d[1]))
            .attr('height', yScale(d[0]) - yScale(d[1]))
        } else {
          bar
            .attr('y', yScale(d[1]))
            .attr('height', yScale(d[0]) - yScale(d[1]))
        }

        self.addBarInteractions(bar, colorScale)
      })
    })
  }

  drawGroupedBars(categoryGroups, x1Scale, yScale, colorScale, innerHeight) {
    const self = this

    categoryGroups.each(function(categoryData) {
      const group = d3.select(this)

      categoryData.values.forEach((val, i) => {
        const bar = group.append('rect')
          .attr('class', `bar bar-${i}`)
          .attr('x', x1Scale(val.name))
          .attr('width', x1Scale.bandwidth())
          .attr('fill', val.color || colorScale(val.name))
          .attr('stroke', colors.neutral[900])
          .attr('stroke-width', 1.5)
          .style('cursor', 'pointer')
          .datum({ ...val, category: categoryData.category })

        if (self.options.animate) {
          bar
            .attr('y', innerHeight)
            .attr('height', 0)
            .transition()
            .duration(animationConfig.duration)
            .delay(i * 50)
            .attr('y', yScale(val.value))
            .attr('height', innerHeight - yScale(val.value))
        } else {
          bar
            .attr('y', yScale(val.value))
            .attr('height', innerHeight - yScale(val.value))
        }

        self.addBarInteractions(bar, colorScale)
      })
    })
  }

  toggleView(view) {
    if (view === this.currentView) return

    this.currentView = view

    // Update Y scale
    const yMax = view === 'stacked' ? this.maxStacked : this.maxGrouped
    this.yScale.domain([0, yMax * 1.1]).nice()

    // Update toggle buttons
    this.g.select('.toggle-stacked rect')
      .transition()
      .duration(200)
      .attr('fill', view === 'stacked' ? colors.neutral[900] : 'transparent')

    this.g.select('.toggle-stacked text')
      .transition()
      .duration(200)
      .attr('fill', view === 'stacked' ? colors.neutral.white : colors.text)

    this.g.select('.toggle-grouped rect')
      .transition()
      .duration(200)
      .attr('fill', view === 'grouped' ? colors.neutral[900] : 'transparent')

    this.g.select('.toggle-grouped text')
      .transition()
      .duration(200)
      .attr('fill', view === 'grouped' ? colors.neutral.white : colors.text)

    // Animate transition
    this.transitionBars(view)
  }

  transitionBars(view) {
    const { g, data, xScale, x1Scale, yScale, colorScale: _colorScale, keys, innerHeight } = this
    const duration = 750

    // Update Y axis
    g.select('.y-axis')
      .transition()
      .duration(duration)
      .call(d3.axisLeft(yScale))

    // Prepare stacked data
    const stackGenerator = d3.stack()
      .keys(keys)
      .value((d, key) => {
        const val = d.values.find((v) => v.name === key)
        return val ? val.value : 0
      })
    const stackedData = stackGenerator(data)

    // Transition bars
    g.selectAll('.category-group').each(function(categoryData, categoryIndex) {
      const group = d3.select(this)
      const bars = group.selectAll('.bar')

      if (view === 'stacked') {
        bars.each(function(d, i) {
          const layer = stackedData[i]
          const stackD = layer[categoryIndex]

          d3.select(this)
            .transition()
            .duration(duration)
            .attr('x', 0)
            .attr('width', xScale.bandwidth())
            .attr('y', yScale(stackD[1]))
            .attr('height', yScale(stackD[0]) - yScale(stackD[1]))
        })
      } else {
        bars.each(function(d, i) {
          const val = categoryData.values[i]

          d3.select(this)
            .transition()
            .duration(duration)
            .attr('x', x1Scale(val.name))
            .attr('width', x1Scale.bandwidth())
            .attr('y', yScale(val.value))
            .attr('height', innerHeight - yScale(val.value))
        })
      }
    })
  }

  addBarInteractions(bar, _colorScale) {
    const self = this

    bar
      .on('mouseover', function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('opacity', 0.8)

        const value = d.value !== undefined ? d.value : d[1] - d[0]
        const name = d.name || d.key
        const content = `
          <strong>${d.category}</strong><br/>
          <span style="color: ${colors.textMuted}">${name}: </span>
          <span style="color: ${colors.accent}">${value}</span>
        `
        showTooltipHtml(self.tooltip, content, event)
      })
      .on('mouseout', function() {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('opacity', 1)
        hideTooltip(self.tooltip)
      })
  }

  renderEmptyState() {
    const emptyDiv = document.createElement('div')
    emptyDiv.className = 'd3-empty-state'
    emptyDiv.innerHTML = `
      <div style="
        display: flex;
        align-items: center;
        justify-content: center;
        height: 200px;
        border: 2px dashed ${colors.neutral[300]};
        color: ${colors.textMuted};
        font-family: ${fonts.family};
        font-size: ${fonts.sizes.base}px;
      ">
        NO DATA AVAILABLE
      </div>
    `
    this.container.appendChild(emptyDiv)
  }

  destroy() {
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize)
    }
    if (this.container) {
      this.container.innerHTML = ''
    }
  }
}
